import { ReactNode, useState, useCallback } from 'react';
import ActionGuard from './ActionGuard';

interface ActionGuardConfig {
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  actionFn: () => void | Promise<void>;
  onCancel?: () => void;
}

export function useActionGuard() {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<ActionGuardConfig | null>(null);

  const open = useCallback((actionGuardConfig: ActionGuardConfig) => {
    setConfig(actionGuardConfig);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setConfig(null);
  }, []);

  // Create the ActionGuard component that renders via portal when needed
  const ActionGuardWrapper = () => {
    if (!isOpen || !config) return null;

    return (
      <ActionGuard
        open={isOpen}
        message={config.message}
        confirmLabel={config.confirmLabel}
        cancelLabel={config.cancelLabel}
        actionFn={config.actionFn}
        onCancel={config.onCancel}
        onClose={close}
      />
    );
  };

  return {
    open,
    close,
    isOpen,
    ActionGuard: ActionGuardWrapper,
  };
}
